import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { useAuth, hasRole } from "../context/AuthContext";
import { api, extractError } from "../lib/api";
import { PageHeader, SectionCard } from "../components/Ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, BookOpen } from "lucide-react";
import { DEFAULT_BARDANA_RULES, BAG_WEIGHT_PRESETS } from "../lib/mandiSettlement";

const KIND_LABEL = {
  FARMER: "Farmer",
  BROKER: "Broker",
  DEALER: "Dealer",
  BUYER: "Buyer",
  SUPPLIER: "Supplier",
  LABOUR_CONTRACTOR: "Labour contractor",
  TRANSPORTER: "Transporter / truck owner",
  OTHER: "Other",
};

const CREATE_KINDS = ["BROKER", "DEALER", "BUYER", "SUPPLIER", "LABOUR_CONTRACTOR", "TRANSPORTER", "OTHER"];
const ALL_KINDS = ["FARMER", ...CREATE_KINDS];

const EXPENSE_PRESETS = [
  { code: "mandi_cess", label: "Mandi Cess", basis: "GROSS_AMOUNT_PCT", rate: 2, amount: 0 },
  { code: "rdf", label: "RDF", basis: "GROSS_AMOUNT_PCT", rate: 2, amount: 0 },
  { code: "commission", label: "Commission", basis: "GROSS_AMOUNT_PCT", rate: 2.5, amount: 0 },
  { code: "transport", label: "Transport (flat / vehicle)", basis: "FIXED_PER_VEHICLE", rate: 0, amount: 0 },
  { code: "loading", label: "Loading / unloading (per bag)", basis: "FIXED_PER_BAG", rate: 0, amount: 0 },
  { code: "handling", label: "Handling (per qtl)", basis: "FIXED_PER_QTL", rate: 0, amount: 0 },
];

const WHEN_OPTS = [
  { value: "ALWAYS", label: "Always" },
  { value: "LAB_REQUIRED", label: "Only if truck lab required" },
  { value: "NO_LAB", label: "Only if truck lab not required" },
  { value: "WITH_CONTRACT", label: "Only with linked contract" },
  { value: "NO_CONTRACT", label: "Only without contract" },
];

const BASIS_OPTS = [
  { value: "GROSS_AMOUNT_PCT", label: "% of gross amount" },
  { value: "FIXED_PER_BAG", label: "Per bag (₹ × bags; 40 kg and 60 kg can differ)" },
  { value: "FIXED_PER_QTL", label: "Per qtl (₹ × billable qtls)" },
  { value: "FIXED_PER_VEHICLE", label: "Flat per vehicle (once per purchase)" },
];

function defaultExpenseRulesTemplate() {
  return EXPENSE_PRESETS.slice(0, 3).map((x) => ({
    code: x.code,
    label: x.label,
    basis: x.basis,
    when: "ALWAYS",
    rate: x.rate,
    amount: x.amount,
    enabled: true,
  }));
}

function emptyRuleRow() {
  return { code: "", label: "", basis: "GROSS_AMOUNT_PCT", when: "ALWAYS", rate: 0, amount: 0, enabled: true };
}

function normalizeRuleBasis(basis) {
  if (basis === "FIXED_PER_ORDER") return "FIXED_PER_VEHICLE";
  return basis || "GROSS_AMOUNT_PCT";
}

function emptyForm() {
  return {
    kind: "BROKER",
    kinds: ["BROKER"],
    display_name: "",
    legal_name: "",
    phone: "",
    email: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state_region: "",
    postal_code: "",
    pan: "",
    gstin: "",
    bank_name: "",
    bank_account_no: "",
    bank_ifsc: "",
    notes: "",
    is_active: true,
    expenseDeductionMode: "default",
    expense_rules: [],
    bardana_rules: { ...DEFAULT_BARDANA_RULES },
  };
}

function normalizeKindsList(kinds, primary) {
  const out = [];
  const seen = new Set();
  for (const raw of [primary, ...(kinds || [])]) {
    const k = String(raw || "").trim().toUpperCase();
    if (!k || seen.has(k) || !ALL_KINDS.includes(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out.length ? out : primary ? [primary] : [];
}

function updateBardanaRule(form, code, patch) {
  const current = form.bardana_rules?.[code] || DEFAULT_BARDANA_RULES[code] || {};
  return {
    ...form.bardana_rules,
    [code]: {
      material_kg_per_bag: current.material_kg_per_bag ?? DEFAULT_BARDANA_RULES[code]?.material_kg_per_bag ?? 0,
      kg_per_bag: current.kg_per_bag ?? 1,
      ...patch,
    },
  };
}

export default function PartyFormPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: editId } = useParams();
  const isNew = location.pathname === "/parties/new";

  const [loading, setLoading] = useState(() => !isNew);
  const [form, setForm] = useState(emptyForm());

  const canUse = hasRole(user, ["admin", "accounts", "accounts_manager"]);
  const kindOptions = !isNew && form.kind === "FARMER" ? ALL_KINDS : isNew ? CREATE_KINDS : ALL_KINDS;
  const kindsList = normalizeKindsList(form.kinds, form.kind);
  const showSupplierDeductions = kindsList.includes("SUPPLIER");
  const extraKindOptions = ALL_KINDS.filter((k) => k !== form.kind);

  useEffect(() => {
    if (!canUse) {
      setLoading(false);
      return;
    }
    if (isNew) {
      setForm(emptyForm());
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .get(`/parties/${editId}`)
      .then((p) => {
        const custom = Array.isArray(p.data.expense_rules);
        const primary = p.data.kind || "BROKER";
        setForm({
          kind: primary,
          kinds: normalizeKindsList(p.data.kinds, primary),
          display_name: p.data.display_name || "",
          legal_name: p.data.legal_name || "",
          phone: p.data.phone || "",
          email: p.data.email || "",
          address_line1: p.data.address_line1 || "",
          address_line2: p.data.address_line2 || "",
          city: p.data.city || "",
          state_region: p.data.state_region || "",
          postal_code: p.data.postal_code || "",
          pan: p.data.pan || "",
          gstin: p.data.gstin || "",
          bank_name: p.data.bank_name || "",
          bank_account_no: p.data.bank_account_no || "",
          bank_ifsc: p.data.bank_ifsc || "",
          notes: p.data.notes || "",
          is_active: !!p.data.is_active,
          expenseDeductionMode: custom ? "custom" : "default",
          expense_rules: custom
            ? (p.data.expense_rules || []).map((r) => {
                const basis = normalizeRuleBasis(r.basis);
                let rate = Number(r.rate) || 0;
                let amount = Number(r.amount) || 0;
                if (basis === "FIXED_PER_QTL" && !rate && amount) {
                  rate = amount;
                  amount = 0;
                }
                if (basis === "FIXED_PER_BAG" && !rate && amount) {
                  rate = amount;
                  amount = 0;
                }
                const bagRates = r.rate_by_bag_kg || {};
                return {
                  ...r,
                  basis,
                  rate,
                  amount,
                  rate_60kg: bagRates["60"] ?? bagRates[60] ?? "",
                  rate_40kg: bagRates["40"] ?? bagRates[40] ?? "",
                };
              })
            : [],
          bardana_rules: p.data.bardana_rules || { ...DEFAULT_BARDANA_RULES },
        });
      })
      .catch((e) => toast.error(extractError(e)))
      .finally(() => setLoading(false));
  }, [canUse, isNew, editId]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.display_name.trim()) return toast.error("Display name is required");
    const { expenseDeductionMode, expense_rules, bardana_rules, ...rest } = form;
    const kinds = normalizeKindsList(form.kinds, form.kind);
    const payload = {
      ...rest,
      kind: kinds[0] || form.kind,
      kinds,
      expense_rules:
        kinds.includes("SUPPLIER") && expenseDeductionMode === "custom"
          ? expense_rules.map((row) => {
              const { rate_60kg, rate_40kg, ...rest } = row;
              const rate_by_bag_kg = {};
              if (rest.basis === "FIXED_PER_BAG") {
                if (rate_60kg !== "" && rate_60kg != null) rate_by_bag_kg["60"] = Number(rate_60kg);
                if (rate_40kg !== "" && rate_40kg != null) rate_by_bag_kg["40"] = Number(rate_40kg);
              }
              return {
                ...rest,
                ...(Object.keys(rate_by_bag_kg).length ? { rate_by_bag_kg } : {}),
              };
            })
          : null,
      bardana_rules: kinds.includes("SUPPLIER") ? bardana_rules : null,
    };
    try {
      if (isNew) {
        await api.post("/parties", payload);
        toast.success("Party created");
      } else {
        await api.patch(`/parties/${editId}`, payload);
        toast.success("Party updated");
      }
      navigate("/parties");
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  if (!canUse) {
    return (
      <div className="space-y-4 px-1">
        <PageHeader eyebrow="Master data" title="Party" description="Restricted to admin and accounts." />
        <p className="text-sm text-muted-foreground">You do not have access.</p>
        <Button asChild variant="outline">
          <Link to="/parties">Back</Link>
        </Button>
      </div>
    );
  }

  if (loading) {
    return <div className="text-sm text-muted-foreground px-1">Loading…</div>;
  }

  return (
    <div className="space-y-6 sm:space-y-8 max-w-4xl mx-auto px-1 sm:px-0" data-testid="party-form-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <Button variant="ghost" size="sm" className="w-fit -ml-2 text-muted-foreground" asChild>
          <Link to="/parties">
            <ArrowLeft className="h-4 w-4 mr-1 inline" /> Back to parties
          </Link>
        </Button>
        {!isNew && editId ? (
          <Button size="sm" variant="secondary" asChild>
            <Link to={`/parties/${editId}/ledger`}>
              <BookOpen className="h-4 w-4 mr-1 inline" /> View ledger
            </Link>
          </Button>
        ) : null}
      </div>

      <PageHeader
        eyebrow="Master data"
        title={isNew ? "New party" : "Edit party"}
        description="Complete profile and banking details. Supplier charge rules (added on top of gross payable) apply only to supplier parties."
      />

      <form onSubmit={save} className="space-y-6">
        <SectionCard title="Basics">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Primary kind" required>
              <Select
                value={form.kind}
                onValueChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    kind: v,
                    kinds: normalizeKindsList([v, ...(f.kinds || []).filter((x) => x !== f.kind)], v),
                    expenseDeductionMode:
                      v !== "SUPPLIER" && !(f.kinds || []).includes("SUPPLIER") ? "default" : f.expenseDeductionMode,
                  }))
                }
              >
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {kindOptions.map((k) => (
                    <SelectItem key={k} value={k}>{KIND_LABEL[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isNew && <p className="text-xs text-muted-foreground mt-1">Farmers are registered at Gate Security.</p>}
            </Field>
            <Field label="Also acts as" className="sm:col-span-2">
              <div className="mt-1.5 flex flex-wrap gap-3">
                {extraKindOptions.map((k) => {
                  const checked = kindsList.includes(k);
                  return (
                    <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(on) => {
                          setForm((f) => {
                            const next = new Set(normalizeKindsList(f.kinds, f.kind));
                            if (on) next.add(k);
                            else next.delete(k);
                            next.add(f.kind);
                            return { ...f, kinds: [...next] };
                          });
                        }}
                      />
                      {KIND_LABEL[k]}
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                A supplier who owns trucks can also be a transporter — one party, multiple roles.
              </p>
            </Field>
            <Field label="Display name" required>
              <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} required className="mt-1.5" />
            </Field>
            <Field label="Legal name" className="sm:col-span-2">
              <Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} className="mt-1.5" />
            </Field>
          </div>
        </SectionCard>

        <SectionCard title="Contact">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
            </Field>
            <Field label="Address line 1" className="sm:col-span-2">
              <Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} className="mt-1.5" />
            </Field>
            <Field label="Address line 2" className="sm:col-span-2">
              <Input value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} className="mt-1.5" />
            </Field>
            <Field label="City">
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5" />
            </Field>
            <Field label="State">
              <Input value={form.state_region} onChange={(e) => setForm({ ...form, state_region: e.target.value })} className="mt-1.5" />
            </Field>
            <Field label="PIN">
              <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="mt-1.5" />
            </Field>
          </div>
        </SectionCard>

        <SectionCard title="Tax & bank">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="PAN">
              <Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} className="mt-1.5" />
            </Field>
            <Field label="GSTIN">
              <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="mt-1.5" />
            </Field>
            <Field label="Bank name" className="sm:col-span-2">
              <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="mt-1.5" />
            </Field>
            <Field label="Account no.">
              <Input value={form.bank_account_no} onChange={(e) => setForm({ ...form, bank_account_no: e.target.value })} className="mt-1.5" />
            </Field>
            <Field label="IFSC">
              <Input value={form.bank_ifsc} onChange={(e) => setForm({ ...form, bank_ifsc: e.target.value })} className="mt-1.5" />
            </Field>
          </div>
        </SectionCard>

        {showSupplierDeductions && (
          <SectionCard title="Mandi bag weights (per party)">
            <p className="text-sm text-muted-foreground mb-4">
              Mandi suppliers differ on how a &quot;60 kg bag&quot; is counted. Set grain (material) kg and bardana kg per bag —
              used for fixed-per-bag settlement and weighbridge reservation.
            </p>
            {(["JUTE_BAG_60KG", "JUTE_BAG_40KG"]).map((bagCode) => {
              const label = bagCode === "JUTE_BAG_60KG" ? "60 kg jute bag" : "40 kg jute bag";
              const row = form.bardana_rules?.[bagCode] || DEFAULT_BARDANA_RULES[bagCode];
              const material = row?.material_kg_per_bag ?? DEFAULT_BARDANA_RULES[bagCode]?.material_kg_per_bag ?? 0;
              const bardana = row?.kg_per_bag ?? 1;
              const presets = BAG_WEIGHT_PRESETS[bagCode] || [];
              return (
                <div key={bagCode} className="mb-6 last:mb-0 rounded-lg border border-border p-4 space-y-3">
                  <p className="text-sm font-medium text-foreground">{label}</p>
                  <div className="flex flex-wrap gap-2">
                    {presets.map((preset) => {
                      const active =
                        Number(material) === Number(preset.material_kg_per_bag)
                        && Number(bardana) === Number(preset.kg_per_bag);
                      return (
                      <Button
                        key={preset.id}
                        type="button"
                        size="sm"
                        variant={active ? "default" : "outline"}
                        className="text-xs h-8"
                        onClick={() =>
                          setForm({
                            ...form,
                            bardana_rules: updateBardanaRule(form, bagCode, {
                              material_kg_per_bag: preset.material_kg_per_bag,
                              kg_per_bag: preset.kg_per_bag,
                            }),
                          })
                        }
                      >
                        {preset.label}
                      </Button>
                      );
                    })}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="Grain / material (kg per bag)">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        className="mt-1.5"
                        value={material}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            bardana_rules: updateBardanaRule(form, bagCode, {
                              material_kg_per_bag: e.target.value,
                            }),
                          })
                        }
                      />
                    </Field>
                    <Field label="Bardana / bag tare (kg per bag)">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        className="mt-1.5"
                        value={bardana}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            bardana_rules: updateBardanaRule(form, bagCode, {
                              kg_per_bag: e.target.value,
                            }),
                          })
                        }
                      />
                    </Field>
                    <Field label="Gross per bag (computed)">
                      <Input
                        className="mt-1.5 bg-muted/40"
                        readOnly
                        value={Math.round((Number(material) + Number(bardana)) * 100) / 100}
                      />
                    </Field>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Fixed-per-bag billable = bags × grain kg. Party bridge must reach grain + bardana ({material} + {bardana} = {Number(material) + Number(bardana)} kg/bag) to qualify.
                  </p>
                </div>
              );
            })}
          </SectionCard>
        )}

        {showSupplierDeductions && (
          <SectionCard title="Supplier — charges added on payable (mandi only)">
            <p className="text-sm text-muted-foreground mb-4">
              Mandi purchase: enabled lines increase net payable (gross + charges).
              TDS sections for these codes are configured under Accounts → TDS Register.
            </p>
            <Field label="Charge schedule">
              <Select
                value={form.expenseDeductionMode}
                onValueChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    expenseDeductionMode: v,
                    expense_rules:
                      v === "custom" && (!f.expense_rules || f.expense_rules.length === 0)
                        ? defaultExpenseRulesTemplate()
                        : f.expense_rules,
                  }))
                }
              >
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">System default (2% + 2% + 2.5% of gross, added on payable)</SelectItem>
                  <SelectItem value="custom">Custom lines</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {form.expenseDeductionMode === "custom" && (
              <div className="mt-4 space-y-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Lines</span>
                  <Button type="button" variant="secondary" size="sm" className="w-full sm:w-auto" onClick={() => setForm({ ...form, expense_rules: [...form.expense_rules, emptyRuleRow()] })}>
                    Add line
                  </Button>
                </div>
                <div className="space-y-4">
                  {form.expense_rules.map((row, i) => (
                    <div key={i} className="rounded-lg border p-3 sm:p-4 space-y-3 bg-muted/20">
                      <div className="flex items-center justify-between gap-2">
                        <Checkbox checked={row.enabled !== false} onCheckedChange={(v) => {
                          const nr = [...form.expense_rules];
                          nr[i] = { ...nr[i], enabled: v === true };
                          setForm({ ...form, expense_rules: nr });
                        }} />
                        <Button type="button" variant="ghost" size="sm" className="text-red-600 shrink-0" onClick={() => setForm({ ...form, expense_rules: form.expense_rules.filter((_, j) => j !== i) })}>Remove</Button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Preset</Label>
                          <Select
                            value="__manual__"
                            onValueChange={(v) => {
                              if (v === "__manual__") return;
                              const preset = EXPENSE_PRESETS.find((x) => x.code === v);
                              if (!preset) return;
                              const nr = [...form.expense_rules];
                              nr[i] = { ...nr[i], code: preset.code, label: preset.label, basis: preset.basis, rate: preset.rate, amount: preset.amount, enabled: true };
                              setForm({ ...form, expense_rules: nr });
                            }}
                          >
                            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Pick…" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__manual__">Manual</SelectItem>
                              {EXPENSE_PRESETS.map((pr) => <SelectItem key={pr.code} value={pr.code}>{pr.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Code</Label>
                          <Input className="mt-1.5 font-mono text-sm" value={row.code} onChange={(e) => {
                            const nr = [...form.expense_rules]; nr[i] = { ...nr[i], code: e.target.value }; setForm({ ...form, expense_rules: nr });
                          }} />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-[10px] text-muted-foreground">Label</Label>
                          <Input className="mt-1.5" value={row.label} onChange={(e) => {
                            const nr = [...form.expense_rules]; nr[i] = { ...nr[i], label: e.target.value }; setForm({ ...form, expense_rules: nr });
                          }} />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-[10px] text-muted-foreground">Basis</Label>
                          <Select value={row.basis || "GROSS_AMOUNT_PCT"} onValueChange={(v) => {
                            const nr = [...form.expense_rules];
                            const next = { ...nr[i], basis: v };
                            if (v === "GROSS_AMOUNT_PCT") next.amount = "";
                            if (v === "FIXED_PER_VEHICLE") next.rate = "";
                            if (v === "FIXED_PER_BAG" || v === "FIXED_PER_QTL") next.amount = "";
                            nr[i] = next;
                            setForm({ ...form, expense_rules: nr });
                          }}>
                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {BASIS_OPTS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        {(row.basis || "GROSS_AMOUNT_PCT") === "GROSS_AMOUNT_PCT" && (
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Percent of gross (%)</Label>
                          <Input type="number" step="0.01" className="mt-1.5" value={row.rate} onChange={(e) => {
                            const nr = [...form.expense_rules]; nr[i] = { ...nr[i], rate: e.target.value }; setForm({ ...form, expense_rules: nr });
                          }} />
                        </div>
                        )}
                        {row.basis === "FIXED_PER_QTL" && (
                        <div>
                          <Label className="text-[10px] text-muted-foreground">₹ per qtl</Label>
                          <Input type="number" step="0.01" className="mt-1.5" value={row.rate} onChange={(e) => {
                            const nr = [...form.expense_rules]; nr[i] = { ...nr[i], rate: e.target.value, amount: "" }; setForm({ ...form, expense_rules: nr });
                          }} />
                        </div>
                        )}
                        {row.basis === "FIXED_PER_BAG" && (
                        <>
                        <div>
                          <Label className="text-[10px] text-muted-foreground">₹ per 60 kg bag</Label>
                          <Input type="number" step="0.01" className="mt-1.5" value={row.rate_60kg ?? ""} onChange={(e) => {
                            const nr = [...form.expense_rules]; nr[i] = { ...nr[i], rate_60kg: e.target.value, amount: "" }; setForm({ ...form, expense_rules: nr });
                          }} />
                        </div>
                        <div>
                          <Label className="text-[10px] text-muted-foreground">₹ per 40 kg bag</Label>
                          <Input type="number" step="0.01" className="mt-1.5" value={row.rate_40kg ?? ""} onChange={(e) => {
                            const nr = [...form.expense_rules]; nr[i] = { ...nr[i], rate_40kg: e.target.value, amount: "" }; setForm({ ...form, expense_rules: nr });
                          }} />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-[10px] text-muted-foreground">₹ per other bag size</Label>
                          <Input type="number" step="0.01" className="mt-1.5" value={row.rate} onChange={(e) => {
                            const nr = [...form.expense_rules]; nr[i] = { ...nr[i], rate: e.target.value, amount: "" }; setForm({ ...form, expense_rules: nr });
                          }} />
                        </div>
                        </>
                        )}
                        {row.basis === "FIXED_PER_VEHICLE" && (
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Flat ₹ per vehicle</Label>
                          <Input type="number" step="0.01" className="mt-1.5" value={row.amount} onChange={(e) => {
                            const nr = [...form.expense_rules]; nr[i] = { ...nr[i], amount: e.target.value, rate: "" }; setForm({ ...form, expense_rules: nr });
                          }} />
                        </div>
                        )}
                        <div className="sm:col-span-2">
                          <Label className="text-[10px] text-muted-foreground">When</Label>
                          <Select value={row.when || "ALWAYS"} onValueChange={(v) => {
                            const nr = [...form.expense_rules]; nr[i] = { ...nr[i], when: v }; setForm({ ...form, expense_rules: nr });
                          }}>
                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {WHEN_OPTS.map((w) => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>
        )}

        <SectionCard title="Notes & status">
          <div className="space-y-4">
            <Field label="Notes">
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1.5" />
            </Field>
            <div className="flex items-center gap-2">
              <Checkbox id="party-active" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: !!v })} />
              <Label htmlFor="party-active" className="text-sm font-normal cursor-pointer">Active</Label>
            </div>
          </div>
        </SectionCard>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sticky bottom-0 bg-muted/95 py-3 border-t border-border -mx-1 px-1 sm:static sm:border-0 sm:bg-transparent sm:p-0">
          <Button type="button" variant="outline" className="w-full sm:w-auto" asChild>
            <Link to="/parties">Cancel</Link>
          </Button>
          <Button type="submit" className="w-full sm:w-auto bg-bansal-sage hover:bg-bansal-sageDark text-white">
            {isNew ? "Create party" : "Save changes"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <div className={className || undefined}>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}
